import { useCallback, useEffect, useRef } from 'react';
import { useAnalyticsContext } from './AnalyticsProvider';
import { Categories } from './categories';
import {
  useGroup,
  useIdentifyAccount,
  useIdentifyProspect,
  useTrack,
  useTrackEvent,
  useUpdateIdentify,
} from './useTrack';

export const useTrackLogin = () => {
  const track = useTrack();
  const identity = useIdentifyAccount();

  return useCallback(
    (account: any) => {
      track('Signed In');
      identity(account);
    },
    [track, identity],
  );
};

interface Signup {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  organizationId?: string;
  phoneNumber: string;
  jobTitle?: string;
  type: 'invited' | 'organic' | 'session' | 'checkout';
  role: 'admin' | 'client' | 'participant';
  roles: string[];
  locale?: string;
  timezone?: string;
}

export const useTrackSignUp = () => {
  const track = useTrack(Categories.SIGNUP);
  const identity = useUpdateIdentify();
  const group = useGroup();

  return useCallback(
    (account: Signup) => {
      identity(account.id, {
        ...account,
        phone: account.phoneNumber,
        title: account.jobTitle,
      });
      if (account.organizationId) {
        group(account.organizationId, {
          id: account.organizationId,
          name: account.organizationName,
        });
      }
      track('Signed Up');
    },
    [track, identity, group],
  );
};

interface Invite {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

export const useTrackInvite = () => {
  const track = useTrack();

  return useCallback(
    (invitee: Invite) => {
      track('Invite Sent', {
        invitation_id: invitee.id,
        invitee_role: invitee.role,
      });
    },
    [track],
  );
};

interface Prospect {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  jobTitle: string;
  companyName: string;
  locale: string;
}

export const useTrackProspect = () => {
  const identify = useIdentifyProspect();
  const track = useTrack();
  return useCallback(
    (allProspect: Prospect) => {
      const { id, ...prospect } = allProspect;
      identify(prospect);
      track('Signed Up As Prospect');
    },
    [track, identify],
  );
};

export interface StandardProductParams {
  product_id: string;
  sku?: string;
  name?: string;
  price?: number;
  quantity?: number;
  category?: string;
  url?: string;
  image_url?: string;
}

export interface StandardPaymentParams {
  checkout_id?: string;
  order_id?: string;
  affiliation?: string;
  total?: number; // total price with credits and taxes
  subtotal?: number; // total price with credits but without taxes
  revenue?: number; // total price without credits and taxes
  fees?: number; // total price without incentive
  tax?: number;
  discount?: number; // number of credits used and discount
  coupon?: string;
  currency?: string;
  products?: StandardProductParams[];
}

export const useTrackQuoteCreation = () => {
  const track = useTrack();
  return useCallback(
    (quote: any, std: StandardPaymentParams) => {
      const { products, prospect, ...data } = quote;
      track('Requested Quote', { ...data, ...std });
    },
    [track],
  );
};

export const useTrackQuoteAcceptation = () => {
  const track = useTrack(Categories.STUDY_CREATION);
  return useCallback(
    (quote: any, std: StandardPaymentParams) => {
      track('Accepted Quote', { ...quote, ...std });
    },
    [track],
  );
};

export const useTrackCalendlyEvent = (category: Categories) => {
  const { analytics } = useAnalyticsContext();

  const categoryRef = useRef<Categories>(category);
  useEffect(() => {
    categoryRef.current = category;
  }, [category]);

  const track = useCallback((event: any) => {
    if (event.origin !== 'https://calendly.com' || !event.data.event) {
      return;
    }

    const eventName = {
      'calendly.event_type_viewed': 'Opened Call Modal',
      'calendly.date_and_time_selected': 'Selected Date For Call',
      'calendly.event_scheduled': 'Booked For Call',
    }[event.data.event as string];

    if (!eventName) {
      return;
    }
    analytics.track(eventName, {
      ...event.data.payload,
      category: categoryRef.current,
    });
  }, []);

  useEffect(() => {
    window.addEventListener('message', track);
    return () => {
      window.removeEventListener('message', track);
    };
  }, [track]);
};

export const useTrackSessionCreationStep = () => {
  const track = useTrack();
  return useCallback(
    (step: string, data: any) => {
      track(`Selected Study ${step}`, data);
    },
    [track],
  );
};
export const useTrackSessionCreated = () => useTrackEvent('Created Session');

export const useTrackSessionStep = () => {
  const track = useTrack();
  return useCallback(
    (step: string, data: any) => {
      track(`Completed Session ${step}`, data);
    },
    [track],
  );
};

export const useTrackJoinedPanel = () => useTrackEvent('Joined Panel');
export const useTrackScreenerFinished = () =>
  useTrackEvent('Screener Finished');
export const useTrackApplied = () => useTrackEvent('Applied');
